import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import {
  PhotoCamera,
  Delete,
  Preview,
  CloudUpload
} from '@mui/icons-material';
import axios from 'axios';

interface PhotoManagerProps {
  cafeId: string;
  photoURLs: string[];
  onPhotosChange: (newPhotoURLs: string[]) => void;
  maxPhotos?: number;
}

const PhotoManager: React.FC<PhotoManagerProps> = ({
  cafeId,
  photoURLs,
  onPhotosChange,
  maxPhotos = 10
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [error, setError] = useState('');

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const remainingSlots = maxPhotos - photoURLs.length;
    const filesToUpload = Array.from(files).slice(0, remainingSlots);

    if (filesToUpload.length === 0) {
      setError(`Maksimum ${maxPhotos} fotoğraf yükleyebilirsiniz`);
      return;
    }

    setError('');
    setUploading(true);
    setUploadProgress(0);

    try {
      // Dosya boyutu ve tip kontrolü
      for (const file of filesToUpload) {
        if (file.size > 5 * 1024 * 1024) {
          throw new Error(`${file.name} çok büyük (Max 5MB)`);
        }
        if (!file.type.startsWith('image/')) {
          throw new Error(`${file.name} geçerli bir resim dosyası değil`);
        }
      }

      // FormData oluştur
      const formData = new FormData();
      formData.append('cafeId', cafeId);
      
      filesToUpload.forEach((file) => {
        formData.append('photos', file);
      });

      // Backend API'ye yükle - check both admin and cafe owner tokens
      const token = localStorage.getItem('adminToken') || localStorage.getItem('cafeOwnerToken');
      const response = await axios.post('https://api.sobcof.com/api/partner-cafes/upload-photos', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      });

      if (response.data.success) {
        const newPhotoURLs = response.data.photoURLs;
        const updatedPhotoURLs = [...photoURLs, ...newPhotoURLs];
        onPhotosChange(updatedPhotoURLs);
      } else {
        throw new Error(response.data.error || 'Fotoğraf yükleme başarısız');
      }

    } catch (error) {
      console.error('Fotoğraf yükleme hatası:', error);
      if (axios.isAxiosError(error)) {
        setError(error.response?.data?.error || 'Fotoğraf yüklenirken hata oluştu');
      } else {
        setError(error instanceof Error ? error.message : 'Fotoğraf yüklenirken hata oluştu');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeletePhoto = async (photoURL: string, index: number) => {
    try {
      // Backend API ile sil - check both admin and cafe owner tokens
      const token = localStorage.getItem('adminToken') || localStorage.getItem('cafeOwnerToken');
      const response = await axios.delete('https://api.sobcof.com/api/partner-cafes/delete-photo', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        data: { photoURL }
      });

      if (response.data.success) {
        // State'den kaldır
        const updatedPhotoURLs = photoURLs.filter((_, i) => i !== index);
        onPhotosChange(updatedPhotoURLs);
      } else {
        throw new Error(response.data.error || 'Fotoğraf silme başarısız');
      }
    } catch (error) {
      console.error('Fotoğraf silme hatası:', error);
      if (axios.isAxiosError(error)) {
        setError(error.response?.data?.error || 'Fotoğraf silinirken hata oluştu');
      } else {
        setError(error instanceof Error ? error.message : 'Fotoğraf silinirken hata oluştu');
      }
    }
  };

  const handlePreview = (photoURL: string) => {
    setPreviewImage(photoURL);
    setPreviewOpen(true);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Fotoğraflar ({photoURLs.length}/{maxPhotos})
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Upload Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          component="label"
          startIcon={<CloudUpload />}
          disabled={uploading || photoURLs.length >= maxPhotos}
          sx={{
            background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #6B3410 0%, #B5551A 100%)'
            }
          }}
        >
          Fotoğraf Yükle
          <input
            type="file"
            hidden
            multiple
            accept="image/*"
            onChange={handleFileSelect}
          />
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {maxPhotos - photoURLs.length} fotoğraf daha ekleyebilirsiniz
        </Typography>
      </Box>

      {/* Upload Progress */}
      {uploading && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="body2" gutterBottom>
              Fotoğraflar yükleniyor...
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={uploadProgress} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              %{Math.round(uploadProgress)}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Photo Grid */}
      {photoURLs.length > 0 ? (
        <ImageList cols={3} gap={8}>
          {photoURLs.map((photoURL, index) => (
            <ImageListItem key={index}>
              <img
                src={photoURL}
                alt={`Kafe fotoğrafı ${index + 1}`}
                loading="lazy"
                style={{ 
                  height: 200, 
                  objectFit: 'cover',
                  borderRadius: 8
                }}
              />
              <ImageListItemBar
                sx={{
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                }}
                title={`Fotoğraf ${index + 1}`}
                position="top"
                actionIcon={
                  <Box>
                    <IconButton
                      sx={{ color: 'white' }}
                      onClick={() => handlePreview(photoURL)}
                    >
                      <Preview />
                    </IconButton>
                    <IconButton
                      sx={{ color: 'white' }}
                      onClick={() => handleDeletePhoto(photoURL, index)}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                }
              />
            </ImageListItem>
          ))}
        </ImageList>
      ) : (
        <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
          <PhotoCamera sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Henüz fotoğraf eklenmemiş
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Yukarıdaki butonu kullanarak fotoğraf ekleyin
          </Typography>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Fotoğraf Önizleme</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center' }}>
            <img
              src={previewImage}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain'
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PhotoManager;
